import React, { useEffect, useState } from 'react';
import { Download, Check } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PWAInstallButtonProps {
  variant?: 'primary' | 'secondary' | 'minimal';
  className?: string;
}

const PWAInstallButton: React.FC<PWAInstallButtonProps> = ({ 
  variant = 'primary',
  className = ''
}) => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if app was installed
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
      }
      
      setDeferredPrompt(null);
    } catch (error) {
      console.error('Error showing install prompt:', error);
    }
  };

  if (isInstalled) {
    return (
      <div className={`flex items-center space-x-2 text-green-600 ${className}`}>
        <Check size={20} />
        <span className="text-sm font-medium">Terinstall</span>
      </div>
    );
  }

  if (!deferredPrompt) {
    return null;
  }

  const getButtonClasses = () => {
    const base = 'flex items-center space-x-2 font-medium rounded-lg transition-colors';
    
    switch (variant) {
      case 'primary':
        return `${base} bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2`;
      case 'secondary':
        return `${base} bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2`;
      case 'minimal':
        return `${base} text-yellow-600 hover:text-yellow-700 px-2 py-1 text-sm`;
      default:
        return base;
    }
  };

  return (
    <button
      onClick={handleInstallClick}
      className={`${getButtonClasses()} ${className}`}
    >
      <Download size={20} />
      <span>Install Aplikasi</span>
    </button>
  );
};

export default PWAInstallButton;
